import { CheckCircle2, FlaskConical, Play, Plus, ShieldAlert, ShieldCheck, Trash2, XCircle } from "lucide-react";
import { formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Field, InlineAlert, Input, Skeleton, Textarea, cn, notifyDone, toast } from "@ncp/ui";
import { useAgentEvaluations, useRedTeamPack, useRunEvaluation, type AgentBody, type AgentDefinition, type AgentEvaluation, type AgentTestCase } from "./api";

type Patch = (p: Partial<AgentBody>, opts?: { transient?: boolean }) => void;
type Expect = AgentTestCase["expect"];

const MAX_CASES = 12;
const splitList = (value: string) => value.split(/\n|,/).map((x) => x.trim()).filter(Boolean);

/**
 * Go-live tests: the agent's own test cases (an input and what the draft must or must not say) plus the standard red-team pack.
 * Releases will not approve this version until its latest evaluation passed.
 */
export function EvaluationTab({ definition, body, patch, readOnly, dirty, onSave }: { definition: AgentDefinition; body: AgentBody; patch: Patch; readOnly: boolean; dirty: boolean; onSave: () => Promise<boolean> }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const evaluations = useAgentEvaluations(definition.key, definition.version);
  const redTeam = useRedTeamPack();
  const run = useRunEvaluation();
  const cases = body.testCases;
  const latest = evaluations.data?.[0];

  const set = (next: AgentTestCase[], transient = false) => patch({ testCases: next }, { transient });
  const edit = (i: number, p: Partial<AgentTestCase>, transient = false) => set(cases.map((c, j) => (j === i ? { ...c, ...p } : c)), transient);
  const editExpect = (i: number, p: Partial<Expect>, transient = false) => edit(i, { expect: { ...cases[i]!.expect, ...p } }, transient);
  const add = () => set([...cases, { name: t("admin:agents.evaluation.newCase", { n: cases.length + 1 }), brief: "", expect: { contains: [], notContains: [] } }]);

  const evaluate = async () => {
    // Unsaved changes are saved first; a failed save (reported by the save) stops here, so the old version is not evaluated instead.
    if (dirty && !(await onSave())) return;
    try {
      const { evaluation } = await run.mutateAsync(definition.id);
      if (evaluation.succeeded) notifyDone(t("admin:agents.evaluation.passedToast"));
      else toast.error(t("admin:agents.evaluation.failedToast"));
    } catch { /* useSave reported the failure with its cause */ }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_420px]">
      <div className="space-y-4">
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2 text-base"><FlaskConical className="size-4" /> {t("admin:agents.evaluation.casesTitle")}</CardTitle>
              <CardDescription>{t("admin:agents.evaluation.casesHint")}</CardDescription>
            </div>
            {!readOnly ? <Button size="sm" variant="outline" onClick={add} disabled={cases.length >= MAX_CASES} data-testid="add-test-case"><Plus /> {t("admin:agents.evaluation.addCase")}</Button> : null}
          </CardHeader>
          <CardContent className="space-y-3">
            {cases.length === 0 ? <EmptyState icon={FlaskConical} title={t("admin:agents.evaluation.noCases")} description={t("admin:agents.evaluation.noCasesBody")} /> : null}
            {cases.map((c, i) => (
              <div key={i} className="rounded-xl border border-sand-200 bg-sand-50/60 p-3" data-testid={`test-case-${i}`}>
                <div className="mb-2 flex items-center gap-2">
                  <Input value={c.name} readOnly={readOnly} onChange={(e) => edit(i, { name: e.target.value }, true)} className="h-8 flex-1 font-medium" aria-label={t("admin:agents.evaluation.name")} data-testid={`test-case-name-${i}`} />
                  {!readOnly ? <Button size="icon-sm" variant="ghost" onClick={() => set(cases.filter((_, j) => j !== i))} aria-label={t("admin:agents.evaluation.remove")}><Trash2 /></Button> : null}
                </div>
                <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_200px]">
                  <Field label={t("admin:agents.evaluation.brief")}>
                    <Textarea value={c.brief ?? ""} readOnly={readOnly} dir="auto" onChange={(e) => edit(i, { brief: e.target.value }, true)} className="min-h-[72px] text-[13px]" placeholder={t("admin:agents.evaluation.briefPlaceholder")} data-testid={`test-case-brief-${i}`} />
                  </Field>
                  <Field label={t("admin:agents.evaluation.matter")} hint={t("admin:agents.evaluation.matterHint")}>
                    <Input value={c.caseNumber ?? ""} readOnly={readOnly} onChange={(e) => edit(i, { caseNumber: e.target.value || undefined }, true)} className="font-mono text-xs" />
                  </Field>
                </div>
                <div className="mt-2 grid gap-3 md:grid-cols-2">
                  <Field label={t("admin:agents.evaluation.contains")} hint={t("admin:agents.evaluation.listHint")}>
                    <Textarea value={(c.expect.contains ?? []).join("\n")} readOnly={readOnly} dir="auto" onChange={(e) => editExpect(i, { contains: splitList(e.target.value) }, true)} className="min-h-[56px] text-[13px]" data-testid={`test-case-contains-${i}`} />
                  </Field>
                  <Field label={t("admin:agents.evaluation.notContains")} hint={t("admin:agents.evaluation.listHint")}>
                    <Textarea value={(c.expect.notContains ?? []).join("\n")} readOnly={readOnly} dir="auto" onChange={(e) => editExpect(i, { notContains: splitList(e.target.value) }, true)} className="min-h-[56px] text-[13px]" />
                  </Field>
                  <Field label={t("admin:agents.evaluation.fields")} hint={t("admin:agents.evaluation.fieldsHint")}>
                    <Input value={(c.expect.fields ?? []).join(", ")} readOnly={readOnly} onChange={(e) => editExpect(i, { fields: splitList(e.target.value) }, true)} className="font-mono text-xs" />
                  </Field>
                  <Field label={t("admin:agents.evaluation.maxLength")}>
                    <Input type="number" min={0} value={c.expect.maxLength ?? ""} readOnly={readOnly} onChange={(e) => editExpect(i, { maxLength: e.target.value ? Number(e.target.value) : undefined })} />
                  </Field>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ShieldAlert className="size-4" /> {t("admin:agents.evaluation.redTeamTitle")}</CardTitle>
            <CardDescription>{t("admin:agents.evaluation.redTeamHint")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {redTeam.isLoading ? <Skeleton className="h-24" /> : (redTeam.data ?? []).map((p) => (
              <div key={p.key} className="rounded-lg border border-sand-200 p-2.5">
                <p className="text-sm font-medium">{t(`admin:agents.evaluation.probes.${p.key}`, { defaultValue: p.name })}</p>
                <p className="mt-0.5 text-xs text-muted-foreground" dir="ltr">“{p.brief}”</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="size-4" /> {t("admin:agents.evaluation.gateTitle")}</CardTitle>
            <CardDescription>{t("admin:agents.evaluation.gateHint")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full" variant="gold" loading={run.isPending} disabled={cases.length === 0} onClick={() => void evaluate()} data-testid="run-evaluation">
              <Play /> {t("admin:agents.evaluation.run", { version: definition.version })}
            </Button>
            {run.isPending ? <InlineAlert tone="info">{t("admin:agents.evaluation.running", { count: cases.length + (redTeam.data?.length ?? 3) })}</InlineAlert> : null}
            {evaluations.isLoading ? <Skeleton className="h-32" /> : latest ? <EvaluationResult evaluation={latest} /> : <p className="text-sm text-muted-foreground">{t("admin:agents.evaluation.never")}</p>}
            {(evaluations.data?.length ?? 0) > 1 ? (
              <div className="border-t border-sand-200 pt-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">{t("admin:agents.evaluation.history")}</p>
                <ul className="space-y-1 text-xs">
                  {evaluations.data!.slice(1).map((e) => (
                    <li key={e.id} className="flex items-center gap-2">
                      {e.succeeded ? <CheckCircle2 className="size-3.5 text-success-600" /> : <XCircle className="size-3.5 text-danger-600" />}
                      <span>{t("admin:agents.evaluation.score", { passed: e.passed, total: e.total, red: e.redTeamPassed, redTotal: e.redTeamTotal })}</span>
                      <span className="ms-auto text-muted-foreground">{formatRelative(e.createdAt, locale)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EvaluationResult({ evaluation }: { evaluation: AgentEvaluation }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  return (
    <div className="space-y-2" data-testid="evaluation-result">
      <div className={cn("flex items-center gap-2 rounded-lg p-2.5 text-sm", evaluation.succeeded ? "bg-success-50 text-success-800" : "bg-danger-50 text-danger-800")}>
        {evaluation.succeeded ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
        <span className="font-medium">{evaluation.succeeded ? t("admin:agents.evaluation.passed") : t("admin:agents.evaluation.failed")}</span>
        <span className="ms-auto text-xs">{t("admin:agents.evaluation.score", { passed: evaluation.passed, total: evaluation.total, red: evaluation.redTeamPassed, redTotal: evaluation.redTeamTotal })}</span>
      </div>
      <p className="text-xs text-muted-foreground">{t("admin:agents.evaluation.runBy", { name: evaluation.runByName ?? "—", when: formatRelative(evaluation.createdAt, locale), seconds: Math.max(1, Math.round(evaluation.durationMs / 1000)) })}</p>
      <ul className="space-y-1.5">
        {(evaluation.results ?? []).map((r, i) => (
          <li key={i} className="rounded-lg border border-sand-200 p-2">
            <div className="flex items-center gap-2 text-sm">
              {r.passed ? <CheckCircle2 className="size-3.5 shrink-0 text-success-600" /> : <XCircle className="size-3.5 shrink-0 text-danger-600" />}
              <span className="truncate font-medium">{r.name}</span>
              <Badge variant={r.kind === "redteam" ? "warning" : "outline"} size="sm" className="ms-auto">{t(`admin:agents.evaluation.kind.${r.kind}`)}</Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground" dir="auto">{r.detail}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}
